import Icon from "../icon";
import Text from "../text";

import type { JSX } from "react";
import type { ButtonProps } from "@/components/types";

/**
 * Button component that renders a standard or custom button with loading state support.
 *
 * @param {ButtonProps} props - The props for the component.
 * @param {React.ReactNode} props.children - The content to be rendered inside the button.
 * @param {boolean} [props.loading] - Indicates if the button is in a loading state.
 * @param {() => void} props.action - Callback function to be executed when the button is clicked.
 * @param {string} props.label - Accessible label and tooltip for the button.
 * @param {string} [props.className] - Additional CSS classes for styling.
 * @param {string} [props.styleText] - CSS classes for styling the inner text span.
 * @returns {JSX.Element} The rendered button component.
 */
const Button = ({
  children,
  loading,
  action,
  label,
  className,
  styleText,
  ...props
}: ButtonProps): JSX.Element => {
  const renderContent = () => {
    if (loading) {
      return <Icon name="spinner" className="fa-spin" aria-label="Loading" />;
    }

    if (typeof children === "string" || label) {
      return (
        <Text className={"text ".concat(styleText ?? "")}>
          {children || label}
        </Text>
      );
    }

    return children;
  };

  return (
    <button
      type={props.type ?? "button"}
      role={props.role ?? "button"}
      aria-busy={loading}
      onClick={action}
      aria-label={label}
      title={label}
      disabled={loading || props.disabled}
      className={(Array.isArray(className)
        ? className.join(" ")
        : className || ""
      ).trim()}
      {...props}
    >
      {renderContent()}
    </button>
  );
};

export default Button;
